import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import ListPage from '@components/common/layout/ListPage';
import { DATE_FORMAT_VALUE, DEFAULT_TABLE_ITEM_SIZE_LARGE, TABLET_DEVICE_SERVICE_QRLIVE } from '@constants';
import apiConfig from '@constants/apiConfig';
import { deviceKinds } from '@constants/masterData';
import useListBase from '@hooks/useListBase';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime } from '@utils';
import { Button } from 'antd';

import useQrliveDeviceFormModal from './useQrliveDeviceFormModal';

const message = defineMessages({
    objectName: 'Devices',
});

const QrLiveDeviceListPage = ({ tabKey, restaurantId, restaurant, savedFilter, onFilterChange }) => {
    const translate = useTranslate();
    const kinhValues = translate.formatKeys(deviceKinds, ['label']);
    const queryParams = new URLSearchParams(location.search);
    const restaurantName = queryParams.get('restaurantName');

    const { data, mixinFuncs, queryFilter, loading, pagination, pagePath } = useListBase({
        apiConfig: apiConfig.device,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(message.objectName),
        },

        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.prepareGetListPathParams = () => {
                return { restaurantId };
            };
            const originalPrepareGetListParams = funcs.prepareGetListParams;
            funcs.prepareGetListParams = (filter) => {
                const params = originalPrepareGetListParams(filter);
                return {
                    ...params,
                    service: TABLET_DEVICE_SERVICE_QRLIVE,
                };
            };
            funcs.getCreateLink = () => {
                return `${pagePath}/qr-live/create?tab=${tabKey}&restaurantId=${restaurantId}&restaurantName=${restaurantName}`;
            };
            funcs.getItemDetailLink = (dataRow) => {
                return `${pagePath}/qr-live/${dataRow.id}?tab=${tabKey}&restaurantId=${restaurantId}&restaurantName=${restaurantName}`;
            };
            funcs.additionalActionColumnButtons = () => ({
                editItem: (record) => (
                    <BaseTooltip type="edit">
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                openEdit(record);
                            }}
                        >
                            <EditOutlined />
                        </Button>
                    </BaseTooltip>
                ),
            });
        },
    });

    const { openCreate, openEdit, renderModals } = useQrliveDeviceFormModal({
        onReload: () => mixinFuncs.getList(),
    });

    const columns = [
        { title: <FormattedMessage defaultMessage="Device Name" />, dataIndex: 'deviceName', width: 200 },
        {
            title: <FormattedMessage defaultMessage="Device Token" />,
            width: 200,
            dataIndex: ['deviceToken'],
        },
        {
            title: <FormattedMessage defaultMessage="Device Kind" />,
            dataIndex: ['deviceKind'],
            render(dataRow) {
                const state = kinhValues.find((item) => item.value == dataRow);
                return <div>{state?.label}</div>;
            },
        },
        {
            title: <FormattedMessage defaultMessage="Expiration date" />,
            width: 180,
            dataIndex: 'expireDate',
            align: 'right',
            render: (createdDate) => {
                const createdDateLocal = convertUtcToLocalTime(createdDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
                return <div>{createdDateLocal}</div>;
            },
        },
        mixinFuncs.renderStatusColumn({ width: 120 }),
        mixinFuncs.renderActionColumn(
            {
                editItem: true,
                delete: true,
            },
            { width: 120 },
        ),
    ];

    return (
        <>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ initialValues: queryFilter })}
                actionBar={
                    mixinFuncs.hasPermission([apiConfig.device.create.baseURL]) && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}>
                            </Button>
                        </div>
                    )
                }
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        pagination={pagination}
                    />
                }
            />
            {renderModals()}
        </>
    );
};

export default QrLiveDeviceListPage;